import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AttendeeFilters = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  totalCount, 
  filteredCount 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const ticketTierOptions = [
    { value: '', label: 'All Ticket Tiers' },
    { value: 'VIP', label: 'VIP' },
    { value: 'Premium', label: 'Premium' },
    { value: 'Standard', label: 'Standard' },
    { value: 'Early Bird', label: 'Early Bird' }
  ];

  const checkInStatusOptions = [
    { value: '', label: 'All Check-in Status' },
    { value: 'checked-in', label: 'Checked In' },
    { value: 'not-checked-in', label: 'Not Checked In' },
    { value: 'late', label: 'Late Arrival' }
  ];

  const paymentStatusOptions = [
    { value: '', label: 'All Payment Status' },
    { value: 'paid', label: 'Paid' },
    { value: 'pending', label: 'Pending' },
    { value: 'refunded', label: 'Refunded' }
  ];

  const paymentMethodOptions = [
    { value: '', label: 'All Payment Methods' },
    { value: 'Credit Card', label: 'Credit Card' },
    { value: 'PayPal', label: 'PayPal' },
    { value: 'Bank Transfer', label: 'Bank Transfer' },
    { value: 'Apple Pay', label: 'Apple Pay' },
    { value: 'Google Pay', label: 'Google Pay' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const getActiveFilterCount = () => {
    return Object.values(filters)?.filter(value => 
      value !== '' && value !== null && value !== undefined
    )?.length;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Filter Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Icon name="Filter" size={20} className="text-muted-foreground" />
              <h3 className="font-medium text-foreground">Filters</h3>
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              Showing {filteredCount} of {totalCount} attendees
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                iconName="X"
                iconPosition="left"
              >
                Clear All
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            >
              {isExpanded ? 'Less' : 'More'} Filters
            </Button>
          </div>
        </div>
      </div>
      {/* Search Bar - Always Visible */}
      <div className="p-4 border-b border-border">
        <Input
          type="search"
          placeholder="Search by name, email, or ticket ID..."
          value={filters?.search || ''}
          onChange={(e) => handleFilterChange('search', e?.target?.value)}
          className="w-full"
        />
      </div>
      {/* Quick Filters - Always Visible */}
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select
            label="Ticket Tier"
            options={ticketTierOptions}
            value={filters?.ticketTier || ''}
            onChange={(value) => handleFilterChange('ticketTier', value)}
          />
          <Select
            label="Check-in Status"
            options={checkInStatusOptions}
            value={filters?.checkInStatus || ''}
            onChange={(value) => handleFilterChange('checkInStatus', value)}
          />
          <Select
            label="Payment Status"
            options={paymentStatusOptions}
            value={filters?.paymentStatus || ''}
            onChange={(value) => handleFilterChange('paymentStatus', value)}
          />
          <Select
            label="Payment Method"
            options={paymentMethodOptions}
            value={filters?.paymentMethod || ''}
            onChange={(value) => handleFilterChange('paymentMethod', value)}
          />
        </div>

        {/* Advanced Filters - Expandable */}
        {isExpanded && (
          <div className="pt-4 border-t border-border space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input
                label="Purchase Date From"
                type="date"
                value={filters?.purchaseDateFrom || ''}
                onChange={(e) => handleFilterChange('purchaseDateFrom', e?.target?.value)}
              />
              <Input
                label="Purchase Date To"
                type="date"
                value={filters?.purchaseDateTo || ''}
                onChange={(e) => handleFilterChange('purchaseDateTo', e?.target?.value)}
              />
              <Input
                label="Amount Range (Min)"
                type="number"
                placeholder="0"
                value={filters?.amountMin || ''}
                onChange={(e) => handleFilterChange('amountMin', e?.target?.value)}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Amount Range (Max)"
                type="number"
                placeholder="1000"
                value={filters?.amountMax || ''}
                onChange={(e) => handleFilterChange('amountMax', e?.target?.value)}
              />
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    // Quick filter for today's check-ins
                    const today = new Date()?.toISOString()?.split('T')?.[0];
                    handleFilterChange('checkInDate', today);
                  }}
                  iconName="Calendar"
                  iconPosition="left"
                  className="w-full"
                >
                  Today's Check-ins
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Filter Summary */}
      {activeFilterCount > 0 && (
        <div className="px-4 pb-4">
          <div className="flex flex-wrap gap-2">
            {filters?.search && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-muted text-muted-foreground">
                <Icon name="Search" size={12} className="mr-1" />
                Search: "{filters?.search}"
                <button
                  onClick={() => handleFilterChange('search', '')}
                  className="ml-2 hover:text-foreground"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            )}
            {filters?.ticketTier && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-muted text-muted-foreground">
                <Icon name="Tag" size={12} className="mr-1" />
                Tier: {filters?.ticketTier}
                <button
                  onClick={() => handleFilterChange('ticketTier', '')}
                  className="ml-2 hover:text-foreground"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            )}
            {filters?.checkInStatus && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-muted text-muted-foreground">
                <Icon name="UserCheck" size={12} className="mr-1" />
                Status: {checkInStatusOptions?.find(opt => opt?.value === filters?.checkInStatus)?.label}
                <button
                  onClick={() => handleFilterChange('checkInStatus', '')}
                  className="ml-2 hover:text-foreground"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            )}
            {filters?.paymentStatus && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-muted text-muted-foreground">
                <Icon name="CreditCard" size={12} className="mr-1" />
                Payment: {paymentStatusOptions?.find(opt => opt?.value === filters?.paymentStatus)?.label}
                <button
                  onClick={() => handleFilterChange('paymentStatus', '')}
                  className="ml-2 hover:text-foreground"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendeeFilters;